<template>
    <div v-if="!isLoadingMe && !isLoadingKam && !isLoadingAppointment" class="appointment form-dimensioning">

        <transition name="fade">
            <div v-if="hasBookedAppointment" class="appointment__overlay">
                <span class="appointment__overlay-intro">{{ t('sie-haben-folgenden-termin-reserviert') }}</span>
                <span class="appointment__overlay-appointment">
                    <span>{{ getFullDate }}</span>
                    <span>{{ getDateByUserAppointment().time }}</span>
                    <span class="appointment__overlay-small" v-if="kamName">{{ t('mit') }}: {{ kamName }}</span>
                    <button-element
                        type="secondary"
                        @click="cancelAppointment"
                        :disabled="isCancelling"
                        :button="{
                            'label': t('termin-stornieren')
                        }"
                    >
                        <span slot="icon" v-if="isCancelling">
                            <loader-element />
                        </span>
                    </button-element>
                </span>
                <div class="buttons">
                    <button-element
                        @click="handleClose"
                        :button="{
                            'label': t('schliessen'),
                        }"
                    ></button-element>
                </div>
            </div>
        </transition>

        <div class="appointment__map">
            <div class="appointment__grid">
                <div class="appointment__map-column">
                    <a :href="appointment.map.url" class="appointment__map-link" target="_blank">
                        <image-element
                            v-if="appointment.map"
                            :image="appointment.map" />
                    </a>
                    <span class="appointment__map-note">{{ t('map-zoom') }}</span>
                </div>

                <div v-if="!isKam && !hasKam" class="appointment__kam-column">
                    <select-element
                        placeholder="KAM auswählen"
                        :selected-value="getKam"
                        :items="getKamItems"
                        :disabled="hasBookedAppointment"
                        @change="kamChanged" />
                </div>

                <div v-else class="appointment__kam-column">
                    <span class="appointment__kam-column-text" v-if="kamName">{{ yourKam }}</span>
                </div>
            </div>
        </div>

        <div class="appointment__notes form-dimensioning">
            <span class="appointment__notes-headline font-h2">{{ t('zusatzinformationen') }}</span>
            <input-element
                name="plusone"
                v-model="kamPlusOne"
                :placeholder="t('plus-one')" />

            <input-element
                name="note"
                :multiline="true"
                v-model="kamNote"
                :placeholder="t('fragen-an-den-account-manager')" />
        </div>

        <div :class="{
                'appointment__book-wrapper': true,
                'appointment__book-wrapper--disabled': bookingDisabled || hasBookedAppointment,
            }"
        >
            <div class="appointment__chose-date">
                <div
                    @click="() => handleDateClicked(index)"
                    v-for="(dateEntry, index) in appointment.dates"
                    :key="`appointment-date-${index}`"
                    :class="{
                        'appointment__calendar-item': true,
                        'appointment__calendar-item--active': activeDateIndex == index,
                    }"
                >
                    <span class="appointment__calendar-item-day">{{ dateEntry.date }}</span>
                    <span class="appointment__calendar-item-weekday">{{ getWeekday(dateEntry.day) }}</span>
                </div>
            </div>

            <div class="appointment__book-date">
                <template v-if="activeDateIndex !== null">
                    <div class="appointment__headline">
                        <span class="appointment__headline-day">{{ getActiveDate.date }}</span>
                        <span class="appointment__headline-weekday">{{ getWeekday(getActiveDate.day) }}</span>
                    </div>
                    <p class="appointment__dates-headline">Verfügbare Termine</p>
                    <div class="appointment__times">
                        <span
                            @click="() => handleTimeClicked(timeIndex)"
                            v-for="(timeData, timeIndex) in getActiveDate.times"
                            :key="`appointment-time-${timeIndex}`"
                            :class="{
                                'appointment__time': true,
                                'appointment__time--active': activeTimeIndex !== null && activeTimeIndex == timeIndex,
                                'appointment__time--reserved': appointmentIsReserved(timeData.timestamp),
                            }">
                                {{ timeData.time }}
                            </span>

                    </div>
                    <div v-if="!hasBookedAppointment" class="appointment__buttons">
                        <button-element
                            @click="bookAppointment"
                            :disabled="activeTimeIndex === null || isBooking"
                            :button="{
                                'label': t('termin-reservieren')
                            }"
                        >
                            <span slot="icon" v-if="isBooking">
                                <loader-element />
                            </span>
                        </button-element>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import store from "@/store";
import {
    UITEXTS_FETCH_NS,
    PAGE_FETCH_NS,
    PAGE_SET_NS,
    KAM_FETCH_NS,
    APPOINTMENT_FETCH_NS,
    APPOINTMENT_BOOKED_NS,
    APPOINTMENT_BOOK_NS,
    APPOINTMENT_CANCEL_NS,
} from "@/store/actions.type";

import ImageElement from '@/components/elements/ImageElement.vue';
import SelectElement from '@/components/elements/form/SelectElement.vue';
import InputElement from '@/components/elements/form/InputElement.vue';
import LoaderElement from '@/components/elements/LoaderElement.vue';

export default {
    name: 'AppointmentElement',
    components: {
        ImageElement,
        SelectElement,
        LoaderElement,
        InputElement,
    },
    props: {
        appointment: {
            type: Object,
            required: true
        }
    },
    computed: {
        ...mapGetters('uitexts', {
            t: 't',
        }),

        ...mapGetters('me', {
            isLoadingMe: 'isLoading',
            isKam: 'isKam',
            hasKam: 'hasKam',
            me: 'getMe',
        }),

        ...mapGetters('kam', {
            isLoadingKam: 'isLoading',
            kams: 'getKams',
            getKamDetails: 'getKamDetails',
        }),

        ...mapGetters('appointment', {
            isLoadingAppointment: 'isLoading',
            isBooking: 'isBooking',
            isCancelling: 'isCancelling',
            appointments: 'getAppointments',
            getUserAppointment: 'getUserAppointment',
            getKam: 'getKam',
            getNote: 'getNote',
            getPlusOne: 'getPlusOne',

        }),

        getActiveDate() {
            return this.activeDateIndex !== null ? this.appointment.dates[this.activeDateIndex] : null;
        },

        getKamItems() {
            if (!!this.kams.length) {

                const kamList = this.kams.map((item) => {
                    return {
                        id: item.email,
                        title: `${item.firstName} ${item.lastName}`
                    };
                });

                return [
                    {
                        id: null,
                        title: 'Bitte wählen',
                        selectable: false
                    },
                    ...kamList
                ];
            }

            return [];
        },

        buttonsDisabled() {
            return this.bookingDisabled || this.isBooking;
        },

        hasBookedAppointment() {
            return this.getUserAppointment != null;
        },

        getFullDate() {
            const date = new Date(this.getUserAppointment * 1000);
            return date.toLocaleDateString();
        },

        kamName() {
            const kamDetails = this.getKamDetails(this.selectedKam);

            if (kamDetails) {
                return `${kamDetails.firstName} ${kamDetails.lastName}`;
            }

            return null;
        },

        yourKam() {
            return this.t('ihr-kam').replace('{kam_name}', this.kamName);
        },
    },

    data: () => {
        return {
            activeDateIndex: null,
            activeTimeIndex: null,
            selectedKam: null,
            bookingDisabled: true,
            kamNote: null,
            kamPlusOne: null,
        }
    },

    async created() {
        await store.dispatch(KAM_FETCH_NS);
        await store.dispatch(APPOINTMENT_BOOKED_NS);

        if (!this.isKam && this.hasKam) {
            this.selectedKam = this.me.participantKAM;
            await store.dispatch(APPOINTMENT_FETCH_NS, this.selectedKam);
            this.bookingDisabled = false;
        }

        if (this.getNote) {
            this.kamNote = this.getNote;
        }

        if (this.getPlusOne) {
            this.kamPlusOne = this.getPlusOne;
        }
    },

    mounted() {

    },

    methods: {
        handleClose() {
            this.$emit('close');
        },

        handleDateClicked(index) {
            if (!this.buttonsDisabled) {
                this.activeTimeIndex = null;
                this.activeDateIndex = index;
            }
        },

        handleTimeClicked(index) {
            if (!this.buttonsDisabled) {
                this.activeTimeIndex = index;
            }
        },

        async cancelAppointment() {

            if (this.isCancelling) { return; }

            const cancellationResult = await store.dispatch(APPOINTMENT_CANCEL_NS, {
                action: 'guestmanagement/appointment/cancel'
            });

            this.activeDateIndex = null;
            this.activeTimeIndex = null;
        },

        async bookAppointment() {
            if (!this.buttonsDisabled) {

                const appointment = this.getActiveDate.times[this.activeTimeIndex];

                const bookingResult = await store.dispatch(APPOINTMENT_BOOK_NS, {
                    kam: this.selectedKam,
                    appointment: appointment.timestamp,
                    note: this.kamNote,
                    plusone: this.kamPlusOne,
                    action: 'guestmanagement/appointment/book'
                });
            }
        },

        async kamChanged(item) {
            if (item && item.id != null) {
                this.bookingDisabled = true;
                this.selectedKam = item.id;
                await store.dispatch(APPOINTMENT_FETCH_NS, this.selectedKam);
                this.bookingDisabled = false;
            } else {
                this.bookingDisabled = true;
                this.selectedKam = null;
            }
        },

        appointmentIsReserved(timestamp) {
            return this.appointments.indexOf(timestamp) !== -1;
        },

        getWeekday(day) {
            return [
                'Montag',
                'Dienstag',
                'Mittwoch',
                'Donnerstag',
                'Freitag',
                'Samstag',
                'Sonntag',
            ][parseInt(day) - 1];
        },

        getDateByUserAppointment() {

            const result = {};
            const date = this.appointment.dates.forEach((date) => {

                date.times.forEach((time) => {
                    if (time.timestamp == this.getUserAppointment) {
                        result['date'] = date['date'];
                        result['time'] = time['time'];
                    }
                })
            });

            return result;
        },
    }
}
</script>
